"use client";

import { useTranslations } from "next-intl";
import { motion } from "framer-motion";
import posthog from "posthog-js";
import { Link } from "@/i18n/routing";
import { SectionReveal } from "@/components/ui/SectionReveal";
import { GradientDivider } from "@/components/ui/GradientDivider";
import { ApplicationFlow } from "@/components/forms/ApplicationFlow";
import { staggerContainer, staggerItem, viewportOnce } from "@/lib/motion";
import { COMPANY_ABOUT, COMPANY_WHY, type JobOpening } from "@/data/jobs";

interface JobContentProps {
  job: JobOpening;
}

/* ──────────────────────────────────────────────
   1. Hero
   ────────────────────────────────────────────── */
function Hero({ job }: JobContentProps) {
  const t = useTranslations("JobPosting");

  return (
    <section className="relative pt-16 lg:pt-36 pb-12 lg:pb-24 px-6 text-center overflow-hidden">
      <motion.div
        className="relative mx-auto max-w-3xl"
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        <motion.div variants={staggerItem}>
          <Link
            href="/careers"
            className="inline-flex items-center gap-2 font-body text-xs uppercase tracking-widest text-text-muted transition-opacity duration-300 hover:text-text-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base active:opacity-70"
          >
            <span aria-hidden="true">&larr;</span> {t("backToCareers")}
          </Link>
        </motion.div>

        <motion.h1
          variants={staggerItem}
          className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-text-primary"
        >
          {job.title}
        </motion.h1>

        <motion.p
          variants={staggerItem}
          className="mt-6 max-w-2xl mx-auto font-body text-base leading-relaxed text-text-body"
        >
          {job.subtitle}
        </motion.p>

        <motion.div variants={staggerItem} className="mt-10">
          <a
            href="#apply"
            onClick={() =>
              posthog.capture("job_apply_intent", { job_slug: job.slug })
            }
            className="inline-flex items-center justify-center rounded-lg bg-bubblegum px-6 py-3 font-body text-xs font-extrabold uppercase tracking-widest text-on-accent transition-transform duration-300 ease-out hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bubblegum focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base active:scale-[0.98]"
          >
            {t("applyCta")}
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
}

/* ──────────────────────────────────────────────
   2. About Role
   ────────────────────────────────────────────── */
function AboutRole({ job }: JobContentProps) {
  const t = useTranslations("JobPosting");

  return (
    <SectionReveal>
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-text-primary">
        {t("aboutTitle")}
      </h2>

      {/* Meta row */}
      <div className="mt-6 flex flex-wrap gap-6">
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full bg-lavender" />
          <span className="font-body text-sm text-text-body">
            {job.location}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full bg-aegean" />
          <span className="font-body text-sm text-text-body">
            {job.language}
          </span>
        </div>
      </div>

      <div className="h-px mt-8 bg-gradient-to-r from-border to-transparent" />

      {/* About Scaffcalc */}
      <div className="mt-10">
        <h3 className="font-display text-xl font-bold text-text-primary">
          {t("aboutScaffcalcTitle")}
        </h3>
        <p className="mt-3 max-w-3xl font-body text-base leading-relaxed text-text-body">
          {COMPANY_ABOUT}
        </p>
      </div>

      {/* Role Description */}
      <div className="mt-10">
        <h3 className="font-display text-xl font-bold text-text-primary">
          {t("roleTitle")}
        </h3>
        <p className="mt-3 max-w-3xl font-body text-base leading-relaxed text-text-body">
          {job.roleDescription}
        </p>
      </div>
    </SectionReveal>
  );
}

/* ──────────────────────────────────────────────
   3. Details — Two Column
   ────────────────────────────────────────────── */
function Details({ job }: JobContentProps) {
  const t = useTranslations("JobPosting");

  return (
    <SectionReveal>
      <div className="grid gap-8 lg:gap-12 lg:grid-cols-2">
        {/* Left — Responsibilities */}
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-text-primary">
            {t("responsibilitiesTitle")}
          </h2>
          <motion.ul
            className="mt-8 flex flex-col gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            {job.responsibilities.map((item, i) => (
              <motion.li key={i} variants={staggerItem} className="flex gap-4">
                <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-bubblegum font-body text-[10px] font-extrabold text-bubblegum">
                  {i + 1}
                </span>
                <div>
                  <h4 className="font-body text-sm font-bold text-text-primary">
                    {item.title}
                  </h4>
                  <p className="mt-1 font-body text-sm leading-relaxed text-text-body">
                    {item.body}
                  </p>
                </div>
              </motion.li>
            ))}
          </motion.ul>
        </div>

        {/* Right — Requirements */}
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-text-primary">
            {t("requirementsTitle")}
          </h2>
          <motion.ul
            className="mt-8 flex flex-col gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            {job.requirements.map((item, i) => (
              <motion.li key={i} variants={staggerItem} className="flex gap-4">
                <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-lavender font-body text-[10px] font-extrabold text-lavender">
                  {i + 1}
                </span>
                <div>
                  <h4 className="font-body text-sm font-bold text-text-primary">
                    {item.title}
                  </h4>
                  <p className="mt-1 font-body text-sm leading-relaxed text-text-body">
                    {item.body}
                  </p>
                </div>
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </div>
    </SectionReveal>
  );
}

/* ──────────────────────────────────────────────
   4. The Offer
   ────────────────────────────────────────────── */
function TheOffer({ job }: JobContentProps) {
  const t = useTranslations("JobPosting");

  return (
    <SectionReveal>
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-text-primary">
        {t("offerTitle")}
      </h2>

      <motion.div
        className="mt-10 flex flex-col gap-0 divide-y divide-border"
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={staggerContainer}
      >
        {job.benefits.map((b, i) => (
          <motion.div
            key={i}
            variants={staggerItem}
            className="flex flex-col md:flex-row md:items-start gap-2 md:gap-8 py-6"
          >
            <h4 className="shrink-0 w-40 font-body text-sm font-bold text-aegean">
              {b.title}
            </h4>
            <p className="font-body text-sm leading-relaxed text-text-body">
              {b.body}
            </p>
          </motion.div>
        ))}
      </motion.div>

      {/* Why Scaffcalc */}
      <div className="mt-12 rounded-2xl border border-border bg-bg-surface p-8 md:p-10">
        <h3 className="font-display text-xl font-bold text-text-primary">
          {t("whyTitle")}
        </h3>
        <p className="mt-3 max-w-2xl font-body text-base leading-relaxed text-text-body">
          {job.why ?? COMPANY_WHY}
        </p>
      </div>
    </SectionReveal>
  );
}

/* ──────────────────────────────────────────────
   5. Summary & Apply
   ────────────────────────────────────────────── */
function SummaryApply({ job }: JobContentProps) {
  const t = useTranslations("JobPosting");

  const rows = [
    { label: t("summaryDepartment"), value: job.department },
    { label: t("summaryLanguage"), value: job.language },
    { label: t("summaryEmployment"), value: job.employment },
    { label: t("summaryLocation"), value: job.location },
    { label: t("summaryRemote"), value: job.remote }
  ];

  return (
    <SectionReveal id="apply" narrow>
      {/* Summary card */}
      <div className="rounded-2xl border border-border bg-bg-surface p-8 md:p-10">
        <div className="divide-y divide-border">
          {rows.map((row) => (
            <div
              key={row.label}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 py-4"
            >
              <span className="font-body text-xs uppercase tracking-widest text-text-muted">
                {row.label}
              </span>
              <span className="font-body text-sm text-text-primary">
                {row.value}
              </span>
            </div>
          ))}
        </div>

        {/* Hiring manager */}
        <div className="mt-8 flex items-center justify-center gap-4">
          <div className="h-10 w-10 rounded-full bg-border flex items-center justify-center font-body text-xs font-bold text-text-muted">
            {job.hiringManager.initials}
          </div>
          <div>
            <p className="font-body text-sm font-bold text-text-primary">
              {job.hiringManager.name}
            </p>
            <p className="font-body text-xs text-text-muted">
              {job.hiringManager.role}
            </p>
          </div>
        </div>
      </div>

      {/* Application form */}
      <div className="mt-12">
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-text-primary">
            {t("applyHeading")}
          </h2>
          <p className="mt-3 max-w-xl mx-auto font-body text-base leading-relaxed text-text-body">
            {t("applySub")}
          </p>
        </div>
        <ApplicationFlow
          presetRole={job.title}
          jobSlug={job.slug}
          jobTitle={job.title}
        />
      </div>
    </SectionReveal>
  );
}

/* ──────────────────────────────────────────────
   6. Catch-All
   ────────────────────────────────────────────── */
function CatchAll() {
  const t = useTranslations("JobPosting");

  return (
    <SectionReveal narrow>
      <div className="rounded-2xl border border-border bg-bg-surface p-10 md:p-16 text-center">
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-text-primary">
          {t("catchAllTitle")}
        </h2>
        <p className="mt-4 max-w-xl mx-auto font-body text-base leading-relaxed text-text-body">
          {t("catchAllBody")}
        </p>
        <div className="mt-8">
          <Link
            href="/careers"
            className="inline-flex items-center justify-center rounded-lg border border-border px-6 py-3 font-body text-xs font-extrabold uppercase tracking-widest text-text-primary transition-transform duration-300 ease-out hover:border-lavender hover:text-lavender hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base active:scale-[0.98]"
          >
            {t("catchAllCta")}
          </Link>
        </div>
      </div>
    </SectionReveal>
  );
}

/* ──────────────────────────────────────────────
   Composed Page
   ────────────────────────────────────────────── */
export function JobContent({ job }: JobContentProps) {
  return (
    <>
      <Hero job={job} />
      <GradientDivider variant={job.accent} />
      <AboutRole job={job} />
      <Details job={job} />
      <TheOffer job={job} />
      <SummaryApply job={job} />
      <CatchAll />
    </>
  );
}
